// src/components/IncomingOrders/OrderMetaCompact.jsx

import { getISOWeek } from "date-fns";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CommentIcon from "@mui/icons-material/Comment";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import {
  Box,
  Chip,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import DebouncedTextField from "./DebouncedTextField";

const useStyles = makeStyles((theme) => ({
  // === “Compact card” – matcha AddressCompact känsla ===
  rootCompact: {
    background: "rgba(0,0,0,0.035)",
    borderRadius: 6,
    padding: "8px 10px",
    width: "100%",
  },
  titleCompact: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
    marginBottom: 1,
    paddingTop: 10,
  },
  rowCompact: {
    display: "flex",
    alignItems: "start",
    gap: "4px",
    padding: "2px 0",
    marginTop: "4px",
    minHeight: 24,
  },
  icon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  labelCompact: {
    fontSize: 12,
    fontWeight: 600,
    minWidth: 160,
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  valueCompact: {
    fontSize: 12,
    color: theme.palette.text.primary,
    flex: 1,
    lineHeight: 1.2,
    minWidth: 0,
  },

  // === “Wide” – matcha AddressInfo/Compare känsla (label/value) ===
  rootWide: {
    background: "rgba(0,0,0,0.02)",
    borderRadius: 6,
    overflow: "hidden",
    width: "100%",
  },
  headWide: {
    padding: "8px 10px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "rgba(0,0,0,0.02)",
  },
  titleWide: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
  },
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.02)",
    },
  },
  labelCell: {
    width: 180,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    verticalAlign: "top",
    borderBottom: "none",
    padding: "6px 10px",
  },
  valueCell: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    verticalAlign: "top",
    borderBottom: "none",
    padding: "6px 10px",
  },

  // Select – tight padding (regression-fix)
  statusSelect: {
    minWidth: 220,
    "& .MuiSelect-select": {
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 10,
      paddingRight: 28, // plats för ikon
      display: "flex",
      alignItems: "center",
      minHeight: 0,
    },
  },

  dirtyField: {
    "&.MuiTextField-root": {
      backgroundColor: "rgba(255, 193, 7, 0.12)",
      boxShadow: "inset 3px 0 0 #f7bd02",
      transition: "background-color 150ms ease",
    },
  },
}));

const formatDateWithWeek = (value) => {
  if (!value) return "";

  const datePart = value.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);

  const d = new Date(year, month - 1, day);

  const week = getISOWeek(d);

  return `${datePart} (vecka ${week})`;
};

const OrderMetaCompact = ({
  order,
  settings = {},
  variant,
  updateField,
  isFieldDirty,
  tableSettings,
  panelWidth,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();

  const [snackOpen, setSnackOpen] = useState(false);

  const emailValue = order?.ordererAddress?.email || order?.email || "";

  const metaRows = useMemo(() => {
    const rows = [];

    const supplierOrderReference = tableSettings?.find(item => item.field === "SUPPLIER_ORDER_REFERENCE");

    if (Object.keys(order).length === 0) {
      return [];
    }

    if (order?.deliveryDateWish) {
      rows.push({
        key: "deliveryDateWish",
        icon: <EventIcon className={classes.icon} />,
        label: intl.formatMessage({
          id: "orderHistory.deliveryDateWish",
          defaultMessage: intl.formatMessage({ id: "incomingOrders.order.requestedDeliveryDate" }),
        }),
        value: formatDateWithWeek(order.deliveryDateWish),
        tooltip: formatDateWithWeek(order.deliveryDateWish),
        enabled: settings.showDeliveryDate !== false,
      });
    }

    rows.push({
      key: "status",
      icon: <CheckCircleIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.status", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.status" }) }),
      value: "__STATUS__",
      enabled: settings.showOrderStatus !== false,
    });

    rows.push({
      key: "costPlaceOrder",
      icon: <BusinessIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.costPlace", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.costCenter" }) }),
      value: order?.costPlaceOrder,
      enabled: settings.showCostCenter !== false,
    });

    rows.push({
      key: "email",
      icon: <EmailIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.email", defaultMessage: "E-post" }),
      value: emailValue,
      enabled: !!settings.showEmail,
    });

    rows.push({
      key: "supplierOrderReference",
      icon: <CommentIcon className={classes.icon} />,
      label: supplierOrderReference?.label ? supplierOrderReference.label
        : intl.formatMessage({ id: "orderHistory.supplierOrderReference", defaultMessage: "KL Orderreferens" }),
      //label: intl.formatMessage({ id: "orderHistory.supplierOrderReference", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.ourReference" }) }),
      value: order?.supplierOrderReference,
      enabled: true,
      editable: true,
      alwaysShow: true,
    });

    rows.push({
      key: "comment",
      icon: <CommentIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.comment", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.message" }) }),
      value: order?.comment,
      enabled: !!settings.showComment,
      multiLine: true
    });

    return rows.filter(
      (r) =>
        r.enabled &&
        (r.alwaysShow || r.value === "__STATUS__" || (r.value !== null && r.value !== undefined && r.value !== ""))
    );
  }, [order, settings, intl, classes.icon, emailValue, tableSettings]);

  if (Object.keys(order).length === 0) return null;

  const StatusControl = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, fontSize: "12px", color: theme.palette.text.primary }}>
      {intl.formatMessage({ id: "incomingOrders.orderstatus." + order.orderStatus })}
    </Box>
  );

  const renderControl = (r) => {
    if (r.key === "status") {
      return (
        StatusControl
      );
    }

    if (r.key === "supplierOrderReference" && r.editable) {
      return (
        <DebouncedTextField
          value={r.value}
          onDebouncedChange={(val) => updateField(r.key, val)}
          forceDirty={isFieldDirty(r.key)}
          dirtyStyle={{
            "&.MuiTextField-root fieldset": {
              backgroundColor: "rgba(255, 193, 7, 0.12)",
              boxShadow: "inset 3px 0 0 #f7bd02",
              transition: "background-color 150ms ease",
            }
          }}
          muiProps={{
            size: "small",
            sx: {
              "& .MuiOutlinedInput-input": { px: 0.25, py: 0 },
              "& .MuiOutlinedInput-notchedOutline": { mx: -0.5, my: -0.25 },
            },
          }}
        />
      );
    }

    const valueNode = (
      <Typography
        className={classes.valueCompact}
        sx={r.multiLine ? { whiteSpace: "pre-line" } : undefined}
      >
        {r.value}
      </Typography>
    );

    return r.tooltip
      ? <Tooltip title={r.tooltip}>{valueNode}</Tooltip>
      : valueNode;
  };

  const StripedWideMetadataRows = ({
    metaRows,
    panelWidth,
    renderControl,
  }) => {

    const COLUMN_OVERHEAD = 55;
    const MIN_COLUMN_WIDTH = 350;
    const ROW_HEIGHT = 30;

    const columnCount = panelWidth
      ? Math.max(
        1,
        Math.floor(
          (COLUMN_OVERHEAD + panelWidth) /
          (COLUMN_OVERHEAD + MIN_COLUMN_WIDTH)
        )
      )
      : null;

    const remainder = metaRows.length % columnCount;

    const lastCellSpan =
      remainder === 0
        ? 1
        : columnCount - remainder + 1;

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: columnCount
            ? `repeat(${columnCount}, minmax(${MIN_COLUMN_WIDTH}px, 1fr))`
            : `repeat(auto-fit, minmax(${MIN_COLUMN_WIDTH}px, 1fr))`,
        }}
      >
        {metaRows.map((r, index) => {
          const isLast = index === metaRows.length - 1;

          return (
            <Box
              key={r.key}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                alignItems: "start",
                px: 1,
                py: 0,
                minHeight: ROW_HEIGHT,

                ...(isLast &&
                  remainder !== 0 && {
                  gridColumn: `span ${lastCellSpan}`,
                }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                {r.icon}

                <Typography
                  className={classes.labelCell}
                  variant="body2"
                  noWrap
                  sx={{ px: 0 }}
                >
                  {r.label}
                </Typography>
              </Box>

              <Box
                className={classes.valueCell}
                sx={{ p: 0, minWidth: 0 }}
              >
                {renderControl(r)}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ====== RENDER: COMPACT ======
  if (variant === "compact") {

    const COMPACT_BREAKPOINT = 900;

    const compactStacked =
      panelWidth != null && panelWidth < COMPACT_BREAKPOINT;

    return (
      <Box className={classes.rootCompact}>
        <Typography className={classes.titleCompact}>
          <FormattedMessage id="orderHistory.orderInformation" defaultMessage="Orderinformation" />
        </Typography>

        {metaRows.map((r) => (
          <Box
            key={r.key}
            className={classes.rowCompact}
            sx={{
              flexDirection: compactStacked ? "column" : "row",
            }}
          >
            <Box sx={{ display: "flex", gap: 1, marginBottom: 0 }}>
              {r.icon}

              <Typography className={classes.labelCompact}>
                {r.label}
              </Typography>
            </Box>
            {renderControl(r)}
          </Box>

        ))}

        {order?.isPartial && (
          <Box sx={{ mt: 0.75 }}>
            <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.partOfLargerOrder" })}>
              <Chip size="small" label="Delad order" color="warning" variant="outlined" />
            </Tooltip>
          </Box>
        )}

        <Snackbar
          open={snackOpen}
          autoHideDuration={2500}
          message={intl.formatMessage({ id: "incomingOrders.order.statusUpdated" })}
          onClose={() => setSnackOpen(false)}
        />
      </Box>
    );
  }

  // ====== RENDER: WIDE ======
  return (
    <Box className={classes.rootWide}>
      <Box className={classes.headWide}>
        <Typography className={classes.titleWide}>
          <FormattedMessage id="orderHistory.orderInformation" defaultMessage="Orderinformation" />
        </Typography>
      </Box>

      <StripedWideMetadataRows
        metaRows={metaRows}
        panelWidth={panelWidth}
        renderControl={renderControl}
      />

      {order?.isPartial && (
        <Box sx={{ p: "8px 10px" }}>
          <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.partOfLargerOrder" })}>
            <Chip size="small" label="Delad order" color="warning" variant="outlined" />
          </Tooltip>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        message={intl.formatMessage({ id: "incomingOrders.order.statusUpdated" })}
        onClose={() => setSnackOpen(false)}
      />
    </Box>
  );
};

OrderMetaCompact.propTypes = {
  order: PropTypes.object.isRequired,
  settings: PropTypes.object,
  variant: PropTypes.oneOf(["compact", "wide"]),
  updateField: PropTypes.func.isRequired,
  isFieldDirty: PropTypes.func.isRequired,
  panelWidth: PropTypes.number,
};

OrderMetaCompact.defaultProps = {
  settings: {},
  variant: "compact",
};

OrderMetaCompact.displayName = "OrderMetaCompact";

export default OrderMetaCompact;
